import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

// ── Gradient Colors - Purple to Green (100% vibrant) ─────────────────────────────
const GRADIENT = {
  purple: 0x8B8BF5,  // Indaco purple
  lime: 0xBFFF00,    // Lime green
};

// ── Create Tangled Knot geometry with gradient colors ────────────────────────────
function createTangledKnot() {
  // TorusKnot with complex twist (p=5, q=3)
  const geometry = new THREE.TorusKnotGeometry(1.0, 0.28, 300, 40, 5, 3);
  
  // Apply purple-green gradient - strong and vibrant
  const colors = [];
  const positions = geometry.attributes.position;
  const purple = new THREE.Color(GRADIENT.purple);
  const lime = new THREE.Color(GRADIENT.lime);
  
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    
    // Create smooth flowing gradient based on position
    const t = (Math.atan2(z, x) + Math.PI) / (2 * Math.PI);
    const yFactor = (y + 1.5) / 3;
    const combined = (t * 0.6 + yFactor * 0.4);
    
    // Sharp blend between purple and lime
    let color;
    if (combined < 0.5) {
      const blend = combined / 0.5;
      color = purple.clone().lerp(lime, blend);
    } else {
      const blend = (combined - 0.5) / 0.5;
      color = lime.clone().lerp(purple, blend);
    }
    colors.push(color.r, color.g, color.b);
  }
  
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return geometry;
}

// ── Purple-Green sparkle particles ──────────────────────────────────────────────
const SPARKLE_COLORS = [0x8B8BF5, 0xA8D8A8, 0xBFFF00, 0x9B9BFF, 0xCFFF4F];

function createSparkle(scene, objectPos) {
  const hex = SPARKLE_COLORS[Math.floor(Math.random() * SPARKLE_COLORS.length)];
  const size = 0.03 + Math.random() * 0.05;
  const geo = new THREE.OctahedronGeometry(size, 0);
  const mat = new THREE.MeshBasicMaterial({
    color: hex,
    transparent: true,
    opacity: 1.0,
  });
  const mesh = new THREE.Mesh(geo, mat);

  const dir = new THREE.Vector3(
    (Math.random() - 0.5) * 2,
    (Math.random() - 0.5) * 2,
    (Math.random() - 0.5) * 2
  ).normalize();
  mesh.position.copy(objectPos).addScaledVector(dir, 1.8 + Math.random() * 0.5);
  scene.add(mesh);

  const speed = 2.0 + Math.random() * 3.0;
  return {
    mesh, mat, geo,
    vx: dir.x * speed + (Math.random() - 0.5) * 0.8,
    vy: dir.y * speed + 0.5 + Math.random() * 0.8,
    vz: dir.z * speed * 0.6,
    rx: (Math.random() - 0.5) * 15,
    ry: (Math.random() - 0.5) * 15,
    life: 1.0,
    decay: 0.012 + Math.random() * 0.018,
  };
}

function updateSparkle(s, delta, scene) {
  s.life -= s.decay;
  s.vy -= delta * 0.4;
  s.mesh.position.x += s.vx * delta;
  s.mesh.position.y += s.vy * delta;
  s.mesh.position.z += s.vz * delta;
  s.mesh.rotation.x += s.rx * delta;
  s.mesh.rotation.y += s.ry * delta;
  const l = Math.max(0, s.life);
  s.mesh.scale.setScalar(l);
  s.mat.opacity = l * l;
  if (s.life <= 0) {
    scene.remove(s.mesh);
    s.geo.dispose();
    s.mat.dispose();
    return true;
  }
  return false;
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function SculptureScene() {
  const mountRef = useRef();

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let width = mount.clientWidth;
    let height = mount.clientHeight;
    let animId;
    let prevTime = performance.now();

    // ── Renderer ──────────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    renderer.toneMapping = THREE.NoToneMapping;
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.touchAction = "none";

    // ── Scene & Camera ────────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 7);

    // ── Studio environment map ────────────────────────────────────────────────
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    // ── Lighting ──────────────────────────────────────────────────────────────
    scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.2));
    
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.5);
    keyLight.position.set(5, 8, 6);
    scene.add(keyLight);
    
    const rimLight = new THREE.DirectionalLight(0xffffff, 1.5);
    rimLight.position.set(-5, -3, 4);
    scene.add(rimLight);

    const frontLight = new THREE.DirectionalLight(0xffffff, 1.0);
    frontLight.position.set(0, 0, 8);
    scene.add(frontLight);

    // ── Orbiting lights - purple and green ────────────────────────────────────
    const orbiters = [
      { color: 0x8B8BF5, intensity: 5, radius: 5, height:  2.5, speed:  0.7, phase: 0 },
      { color: 0xBFFF00, intensity: 5, radius: 4.5, height: -2, speed: -0.6, phase: Math.PI * 0.67 },
      { color: 0x8B8BF5, intensity: 5, radius: 5.5, height:  0.5, speed:  0.5, phase: Math.PI * 1.33 },
    ];
    const lights = orbiters.map((o) => {
      const l = new THREE.PointLight(o.color, o.intensity, 20);
      scene.add(l);
      return l;
    });

    // ── Tangled Knot - Metallic Material ────────────────────────────────────────
    const knotGeometry = createTangledKnot();
    const knotMaterial = new THREE.MeshStandardMaterial({
      vertexColors: true,
      metalness: 0.85,
      roughness: 0.15,
      envMapIntensity: 1.2,
    });
    const knot = new THREE.Mesh(knotGeometry, knotMaterial);
    scene.add(knot);

    // ── Float / interaction state ─────────────────────────────────────────────
    let floatTime = 0;
    let currX = 0, currY = 0;
    let isDragging = false, isHovered = false, isResting = false;
    let restTimer = null;
    let dragOffX = 0, dragOffY = 0;

    // ── Sparkle state ─────────────────────────────────────────────────────────
    const sparkles = [];
    let sparkleTimer = 0;
    let nextSparkleInt = 0.8 + Math.random() * 1.5;

    // ── Raycasting ────────────────────────────────────────────────────────────
    const dragPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const raycaster = new THREE.Raycaster();
    const ndcVec = new THREE.Vector2();
    const hitVec = new THREE.Vector3();

    const getWorldPos = (cx, cy) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndcVec.x = ((cx - rect.left) / rect.width) * 2 - 1;
      ndcVec.y = -((cy - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndcVec, camera);
      raycaster.ray.intersectPlane(dragPlane, hitVec);
      return { x: hitVec.x, y: hitVec.y };
    };

    const hitsObject = (cx, cy) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndcVec.x = ((cx - rect.left) / rect.width) * 2 - 1;
      ndcVec.y = -((cy - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndcVec, camera);
      return raycaster.intersectObject(knot, false).length > 0;
    };

    // ── Pointer events ────────────────────────────────────────────────────────
    const onPointerDown = (e) => {
      if (!hitsObject(e.clientX, e.clientY)) return;
      isDragging = true;
      isResting = false;
      if (restTimer) clearTimeout(restTimer);
      const pos = getWorldPos(e.clientX, e.clientY);
      dragOffX = pos.x - currX;
      dragOffY = pos.y - currY;
      // Add dragging class for cursor 3
      document.body.classList.add('is-dragging');
      e.preventDefault();
    };
    const onPointerMove = (e) => {
      if (isDragging) {
        const pos = getWorldPos(e.clientX, e.clientY);
        currX = pos.x - dragOffX;
        currY = pos.y - dragOffY;
        return;
      }
      const hit = hitsObject(e.clientX, e.clientY);
      if (hit !== isHovered) {
        isHovered = hit;
        // Add/remove hover class for cursor 2
        if (isHovered) {
          document.body.classList.add('sculpture-hover');
        } else {
          document.body.classList.remove('sculpture-hover');
        }
      }
    };
    const onPointerUp = () => {
      if (!isDragging) return;
      isDragging = false;
      // Remove dragging class
      document.body.classList.remove('is-dragging');
      isResting = true;
      if (restTimer) clearTimeout(restTimer);
      restTimer = setTimeout(() => {
        isResting = false;
      }, 2000);
    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    // ── Animation loop ────────────────────────────────────────────────────────
    let elapsed = 0;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const now = performance.now();
      const delta = Math.min((now - prevTime) / 1000, 0.05);
      prevTime = now;
      elapsed += delta;

      // Orbit neon lights around the knot
      orbiters.forEach((o, i) => {
        const a = elapsed * o.speed + o.phase;
        lights[i].position.set(
          knot.position.x + Math.cos(a) * o.radius,
          knot.position.y + o.height + Math.sin(a * 0.6) * 1.5,
          knot.position.z + Math.sin(a) * o.radius
        );
      });

      // Knot rotation (pause during drag)
      if (!isDragging) {
        knot.rotation.y += delta * 0.3;
        knot.rotation.x += delta * 0.15;
        knot.rotation.z += delta * 0.1;
      }

      // Float position
      if (isDragging) {
        knot.position.x = currX;
        knot.position.y = currY;
      } else if (isResting) {
        floatTime += delta * 0.35;
      } else {
        if (!isHovered) floatTime += delta * 0.35;
        const t = floatTime;
        const tx = Math.sin(t * 0.55) * 2.2 + Math.sin(t * 1.2 + 0.9) * 0.6 + Math.sin(t * Math.PI * 0.38 + 1.7) * 0.25;
        const ty = Math.cos(t * 0.42 + 0.4) * 1.1 + Math.cos(t * 0.87 + 1.1) * 0.4 + Math.cos(t * 1.65 + 0.6) * 0.18;
        currX += (tx - currX) * 0.025;
        currY += (ty - currY) * 0.025;
        knot.position.x = currX;
        knot.position.y = currY;
      }

      // Sparkles
      sparkleTimer += delta;
      if (sparkleTimer >= nextSparkleInt) {
        sparkleTimer = 0;
        nextSparkleInt = 0.6 + Math.random() * 1.2;
        const count = 5 + Math.floor(Math.random() * 6);
        for (let k = 0; k < count; k++) {
          sparkles.push(createSparkle(scene, knot.position));
        }
      }
      for (let i = sparkles.length - 1; i >= 0; i--) {
        if (updateSparkle(sparkles[i], delta, scene)) sparkles.splice(i, 1);
      }

      renderer.render(scene, camera);
    };
    animate();

    // ── Resize ────────────────────────────────────────────────────────────────
    const onResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener("resize", onResize);

    // ── Cleanup ───────────────────────────────────────────────────────────────
    return () => {
      cancelAnimationFrame(animId);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("resize", onResize);
      if (restTimer) clearTimeout(restTimer);
      // Remove cursor classes
      document.body.classList.remove('sculpture-hover');
      document.body.classList.remove('is-dragging');
      sparkles.forEach((s) => {
        scene.remove(s.mesh);
        s.geo.dispose();
        s.mat.dispose();
      });
      knotGeometry.dispose();
      knotMaterial.dispose();
      renderer.dispose();
      if (mount?.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div
      ref={mountRef}
      data-testid="sculpture-canvas-inner"
      aria-label="Interactive 3D Tangled Knot"
      style={{ position: "absolute", inset: 0, touchAction: "none" }}
    />
  );
}
